(function (root) {
  'use strict';
  const campaign = root.document?.getElementById('tk-cart-config');
  const holiday = campaign ? JSON.parse(campaign.textContent).campaign === 'holiday' : false;
  const marker = holiday ? 'holiday-2026' : 'launch-2026';
  const property = '_tk_launch_gift';
  const optOut = '_tk_launch_gift_opt_out';
  const tiers = holiday ? [{ minimum: 9000, key: 'lip' }] : [{ minimum: 5000, key: 'lip' }, { minimum: 10000, key: 'duo' }];
  const isGift = item => ['launch-2026', 'holiday-2026'].includes(item.properties?.[property]);
  const paidSubtotal = cart => {
    const purchased = cart.items.reduce((sum, item) => sum + (isGift(item) || item.gift_card ? 0 : (holiday ? item.final_line_price : item.original_line_price)), 0);
    if (!holiday || !Number.isFinite(cart.total_price)) return purchased;
    const excluded = cart.items.reduce((sum, item) => sum + (isGift(item) || item.gift_card ? item.final_line_price : 0), 0);
    return Math.max(0, Math.min(purchased, cart.total_price - excluded));
  };
  const tierFor = amount => [...tiers].reverse().find(tier => amount >= tier.minimum) || null;
  const paidDiscount = cart => cart.items.some(item => !isGift(item) && item.final_line_price < item.original_line_price) || (cart.cart_level_discount_applications || []).length > 0;
  const discountCodes = cart => {
    const codes = new Map();
    const add = code => { if (code) codes.set(String(code).toLowerCase(), String(code)); };
    (cart.discount_codes || []).forEach(item => add(item.code));
    (cart.cart_level_discount_applications || []).forEach(item => { if (item.type === 'discount_code') add(item.title); });
    cart.items.forEach(item => (item.line_level_discount_allocations || []).forEach(allocation => {
      const application = allocation.discount_application;
      if (application?.type === 'discount_code') add(application.title);
    }));
    return [...codes.values()];
  };

  class GiftError extends Error {
    constructor(code, cart) { super(code); this.code = code; this.cart = cart; }
  }

  class Engine {
    constructor(config, request) { this.config = config; this.request = request; }
    async read() { return this.request('cart.js'); }
    async setCode(cart, code = '', updates = {}, force = false) {
      const managed = [...Object.values(this.config.gifts).map(gift => gift.discountCode), ...(this.config.legacyCodes || [])].filter(Boolean).map(code => code.toLowerCase());
      const current = discountCodes(cart);
      const next = current.filter(value => !managed.includes(value.toLowerCase()));
      if (code) next.push(code);
      if (!force && !Object.keys(updates).length && current.length === next.length && current.every((value, index) => value.toLowerCase() === next[index].toLowerCase())) return cart;
      return this.request('cart/update.js', { discount: next.join(','), updates });
    }
    async clean(cart, keep) {
      const updates = Object.fromEntries(cart.items.filter(item => isGift(item) && item.key !== keep).map(item => [item.key, 0]));
      if (keep) {
        if (Object.keys(updates).length) cart = await this.request('cart/update.js', { updates });
      } else cart = await this.setCode(cart, '', updates);
      if (cart.items.some(item => isGift(item) && item.key !== keep)) throw new GiftError('gift_error', cart);
      return cart;
    }
    async sync(cart) {
      cart = cart || await this.read();
      // An unpublished holiday preview must not modify an existing live cart.
      if (holiday && this.config.mode !== 'active') return { cart, tier: tierFor(paidSubtotal(cart)), state: paidDiscount(cart) ? 'discount' : 'preview' };
      if (paidDiscount(cart)) cart = await this.clean(cart);
      const tier = tierFor(paidSubtotal(cart));
      const gift = tier && this.config.gifts[tier.key];
      const skipped = cart.attributes?.[optOut] === '1';
      if (this.config.mode !== 'active' || cart.currency !== 'USD' || !tier || skipped) {
        return { cart: await this.clean(cart), tier, state: skipped ? 'skipped' : this.config.mode };
      }
      if (paidDiscount(cart)) return { cart: await this.clean(cart), tier, state: 'discount' };
      if (!gift?.available) return { cart: await this.clean(cart), tier, state: 'unavailable' };
      if (!gift.discountCode) throw new GiftError('gift_error', await this.clean(cart));
      const existing = cart.items.find(item => isGift(item) && String(item.variant_id) === String(gift.id) && item.quantity === 1 && !item.selling_plan_allocation);
      if (existing) cart = await this.clean(cart, existing.key);
      else if (cart.items.some(item => isGift(item) && String(item.variant_id) === String(gift.id))) cart = await this.clean(cart);
      const updates = Object.fromEntries(cart.items.filter(item => isGift(item) && item.key !== existing?.key).map(item => [item.key, 0]));
      if (!existing) {
        try {
          await this.request('cart/add.js', { items: [{ id: gift.id, quantity: 1, properties: { [property]: marker } }] });
        } catch (error) {
          cart = await this.read();
          cart = await this.clean(cart);
          throw new GiftError(error.status === 422 ? 'unavailable' : 'gift_error', cart);
        }
      }
      try {
        cart = await this.setCode(cart, gift.discountCode, updates, !existing);
      } catch {
        cart = await this.clean(await this.read());
        throw new GiftError('gift_error', cart);
      }
      const gifts = cart.items.filter(isGift);
      const correct = gifts.length === 1 && gifts[0].quantity === 1 && String(gifts[0].variant_id) === String(gift.id) && gifts[0].final_line_price === 0;
      if (!correct || tierFor(paidSubtotal(cart))?.key !== tier.key || paidDiscount(cart)) {
        cart = await this.clean(cart);
        throw new GiftError('gift_error', cart);
      }
      return { cart, tier, state: 'added' };
    }
    async setOptOut(value) {
      const cart = await this.request('cart/update.js', { attributes: { [optOut]: value ? '1' : '' } });
      return this.sync(cart);
    }
  }
  const api = { Engine, GiftError, marker, property, optOut, tiers, isGift, paidSubtotal, tierFor, paidDiscount, discountCodes };
  root.TKLaunchGift = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(globalThis);
